import {
  Box,
  Grid,
  Toolbar,
  Typography,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import {
  CloudDownloadOutlined,
  Visibility,
  Close,
  KeyboardArrowLeftOutlined,
  ImageOutlined,
} from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function DamageReport() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [loading, setLoading] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(new Date());

  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");

  const [damageRows, setDamageRows] = useState([]);
  const [damageReportData, setDamageReportData] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [previewImage, setPreviewImage] = useState(null);

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const [deletingId, setDeletingId] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const fixAttachmentUrl = (url = "") => {
    if (!url) return "";

    return url
      .replace("cloudfront.net", "cloudfront.net/")
      .replace("cloudfront.net//", "cloudfront.net/");
  };

  const getEntryType = (entry) => entry?.entryType || "PRODUCT";

  const isProductEntry = (entry) => getEntryType(entry) === "PRODUCT";

  const getEntryName = (entry) => {
    if (isProductEntry(entry)) {
      return entry?.product?.name || "N/A";
    }

    return entry?.rawMaterial?.name || "N/A";
  };

  const getEntryUnit = (entry) => {
    if (isProductEntry(entry)) {
      return entry?.product?.weightUnit || "";
    }

    return entry?.rawMaterial?.unit || "";
  };

  const getEntryAttachments = (entry) => {
    const attachments = entry?.attachemnts || entry?.attachments || [];

    return attachments.map((item) => ({
      ...item,
      attachment: fixAttachmentUrl(item?.attachment),
    }));
  };

  const getEntryDate = (entry) => {
    return entry?.date ? new Date(entry.date).toISOString().split("T")[0] : "N/A";
  };

  const getEntryPrice = (entry) => {
    if (!isProductEntry(entry)) return 0;

    return Number(entry?.product?.offerPrice ?? entry?.product?.mrp ?? 0);
  };

  const getEntryValue = (entry) => {
    const qty = Number(entry?.quantity || 0);
    const price = getEntryPrice(entry);

    return qty * price;
  };

  const handleOpenModal = (entry) => {
    setSelectedEntry(entry);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEntry(null);
  };

  const openDeleteDialog = (entry) => {
    if (!entry?._id) return;

    setDeleteCandidate(entry);
    setOpenDeleteConfirm(true);
  };

  const closeDeleteDialog = () => {
    if (deletingId && deleteCandidate?._id === deletingId) return;

    setOpenDeleteConfirm(false);
    setDeleteCandidate(null);
  };

  const getDamageReport = async (download = false) => {
    if (!outletData?._id) return;

    setLoading(true);

    try {
      const date = format(selectedStartDate, "yyyy-MM-dd");

      const params = {
        startDate: date,
        endDate: date,
        outlet: outletData._id,
      };

      if (download) params.isDownload = "true";

      const response = await axios.get(`${API.BASE_URL}damaged-product-entry`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: download ? "blob" : "json",
      });

      if (download) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const selectedOutletName =
          outletData?.name || outletData?.outletName || "Outlet";

        const fileName = `${selectedOutletName} Consolidated Damage Report (${date}).xlsx`;

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success("Consolidated damage report downloaded successfully!", {
          position: "bottom-center",
          autoClose: 3000,
        });

        return;
      }

      const data = response?.data?.data || {};

      const productRows = Array.isArray(data?.dbResponse) ? data.dbResponse : [];
      const rawMaterialRows = Array.isArray(data?.damagedRawMaterialEntries)
        ? data.damagedRawMaterialEntries
        : [];

      const formattedProducts = productRows.map((item) => ({
        ...item,
        entryType: "PRODUCT",
      }));

      const formattedRawMaterials = rawMaterialRows.map((item) => ({
        ...item,
        entryType: "RAW_MATERIAL",
      }));

      const mergedRows = [...formattedProducts, ...formattedRawMaterials].sort(
        (a, b) => new Date(b?.date || 0) - new Date(a?.date || 0)
      );

      setDamageReportData(data);
      setDamageRows(mergedRows);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error:", error);

      toast.error(download ? "Failed to download report" : "Failed to fetch report", {
        autoClose: 3000,
      });

      if (!download) {
        setDamageRows([]);
        setDamageReportData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const performDeleteDamageEntry = async (entry) => {
    const id = entry?._id;
    if (!id) return;

    setDeletingId(id);

    try {
      await axios.delete(`${API.BASE_URL}damaged-product-entry`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          _id: id,
          entryType: getEntryType(entry),
        },
      });

      setDamageRows((prev) =>
        Array.isArray(prev) ? prev.filter((x) => x?._id !== id) : prev
      );

      if (selectedEntry?._id === id) handleCloseModal();

      toast.success("Entry deleted successfully!", { autoClose: 2500 });
      closeDeleteDialog();
      getDamageReport(false);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete entry", { autoClose: 3000 });
    } finally {
      setDeletingId(null);
    }
  };

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(damageRows) ? damageRows : [];
    const q = searchText.trim().toLowerCase();

    return rows.filter((r) => {
      const entryType = getEntryType(r);

      if (typeFilter !== "ALL" && entryType !== typeFilter) {
        return false;
      }

      if (!q) return true;

      const productName = (r?.product?.name || "").toLowerCase();
      const rawMaterialName = (r?.rawMaterial?.name || "").toLowerCase();
      const remark = (r?.remark || "").toLowerCase();
      const outletName = (r?.outlet?.name || "").toLowerCase();
      const unit = getEntryUnit(r).toLowerCase();

      const addonText = (r?.addons || [])
        .map((a) => `${a?.addonTitle || ""} ${a?.addonValueTitle || ""}`.trim())
        .join(" ")
        .toLowerCase();

      const dateStr = r?.date ? String(r.date).toLowerCase() : "";

      return (
        productName.includes(q) ||
        rawMaterialName.includes(q) ||
        remark.includes(q) ||
        outletName.includes(q) ||
        addonText.includes(q) ||
        unit.includes(q) ||
        dateStr.includes(q)
      );
    });
  }, [damageRows, searchText, typeFilter]);

  const legendTotals = useMemo(() => {
    const rows = Array.isArray(filteredRows) ? filteredRows : [];

    return rows.reduce(
      (acc, r) => {
        const qty = Number(r?.quantity || 0);
        const attachmentsCount = getEntryAttachments(r).length;

        acc.totalEntries += 1;
        acc.totalQty += qty;
        acc.totalAttachments += attachmentsCount;

        if (isProductEntry(r)) {
          acc.productEntries += 1;
          acc.productQty += qty;
          acc.totalValue += getEntryValue(r);
        } else {
          acc.rawMaterialEntries += 1;
          acc.rawMaterialQty += qty;
        }

        return acc;
      },
      {
        totalEntries: 0,
        productEntries: 0,
        rawMaterialEntries: 0,
        totalQty: 0,
        productQty: 0,
        rawMaterialQty: 0,
        totalValue: 0,
        totalAttachments: 0,
      }
    );
  }, [filteredRows]);

  const totalPages = Math.ceil((filteredRows?.length || 0) / itemsPerPage) || 1;

  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getDamageReport(false);
  }, []);

  const candidateName = getEntryName(deleteCandidate);

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <KeyboardArrowLeftOutlined
                    sx={{ cursor: "pointer", mt: 0.2 }}
                    onClick={() => navigate(-1)}
                    fontSize="large"
                  />

                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#0f172a" }}>
                      Damage Report
                    </Typography>

                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                      Products and raw materials damage entries in a report.
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    icon={<FilterAltOutlined />}
                    label={`Records: ${filteredRows?.length || 0}`}
                    sx={{ fontWeight: 800, borderRadius: 2 }}
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                    Date
                  </Typography>

                  <DatePicker
                    selected={selectedStartDate}
                    onChange={(date) => {
                      setSelectedStartDate(date);
                      setCurrentPage(1);
                    }}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedStartDate ? format(selectedStartDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                    Search
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search product & raw material"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                    Type
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Chip
                      label="All"
                      clickable
                      onClick={() => {
                        setTypeFilter("ALL");
                        setCurrentPage(1);
                      }}
                      color={typeFilter === "ALL" ? "primary" : "default"}
                      sx={{ fontWeight: 800 }}
                    />

                    <Chip
                      label="Product"
                      clickable
                      onClick={() => {
                        setTypeFilter("PRODUCT");
                        setCurrentPage(1);
                      }}
                      color={typeFilter === "PRODUCT" ? "primary" : "default"}
                      sx={{ fontWeight: 800 }}
                    />

                    <Chip
                      label="Raw Materials"
                      clickable
                      onClick={() => {
                        setTypeFilter("RAW_MATERIAL");
                        setCurrentPage(1);
                      }}
                      color={typeFilter === "RAW_MATERIAL" ? "secondary" : "default"}
                      sx={{ fontWeight: 800 }}
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                      color: "#334155",
                      mb: 0.8,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Typography>

                  <Stack direction="row" justifyContent="center" spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={() => getDamageReport(false)}
                      sx={{
                        height: 52,
                        width: "40%",
                        borderRadius: 2,
                        fontWeight: 900,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Get"}
                    </Button>

                    <Tooltip title="Download Excel">
                      <CloudDownloadOutlined
                        onClick={() => filteredRows?.length > 0 && getDamageReport(true)}
                        sx={{
                          fontSize: "30px",
                          cursor: filteredRows?.length > 0 ? "pointer" : "not-allowed",
                          color: filteredRows?.length > 0 ? "#003049" : "#94a3b8",
                          display: "flex",
                          alignSelf: "center",
                        }}
                      />
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 800 }}>
                    Fetching Consolidated Damage Report...
                  </Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
                    No records to display
                  </Typography>

                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Select date and click “Get” to view results.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          label={`Total Rows: ${legendTotals.totalEntries}`}
                          variant="outlined"
                          sx={{ fontWeight: 900, borderRadius: 2 }}
                        />

                        <Chip
                          label={`Products: ${legendTotals.productEntries}`}
                          variant="outlined"
                          color="primary"
                          sx={{ fontWeight: 900, borderRadius: 2 }}
                        />

                        <Chip
                          label={`Raw Materials: ${legendTotals.rawMaterialEntries}`}
                          variant="outlined"
                          color="secondary"
                          sx={{ fontWeight: 900, borderRadius: 2 }}
                        />

                        <Chip
                          label={`Total Qty: ${legendTotals.totalQty}`}
                          variant="outlined"
                          sx={{ fontWeight: 900, borderRadius: 2 }}
                        />
                      </Stack>

                      <Chip
                        label={`Product Value: ₹${legendTotals.totalValue.toFixed(2)}`}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: 0, maxHeight: "70vh", overflow: "auto" }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Date", "Type", "Name", "Qty", "Remark", "Detail", "Delete"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={["Name", "Remark"].includes(h) ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((row, index) => {
                          const isProduct = isProductEntry(row);
                          const qty = Number(row?.quantity || 0);
                          const name = getEntryName(row);
                          const isDeletingThis = deletingId === row?._id;

                          return (
                            <TableRow
                              key={(row?._id || index) + "-" + index}
                              hover
                              sx={{
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                {getEntryDate(row)}
                              </TableCell>

                              <TableCell align="center">
                                <Chip
                                  label={isProduct ? "Product" : "Raw Material"}
                                  color={isProduct ? "primary" : "secondary"}
                                  size="small"
                                  sx={{ fontWeight: 900, borderRadius: 2 }}
                                />
                              </TableCell>

                              <TableCell align="left">
                                <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
                                  {name}
                                </Typography>
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                {qty}
                              </TableCell>

                              <TableCell align="left" sx={{ maxWidth: 480 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: "#334155",
                                    fontWeight: 700,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {row?.remark || "—"}
                                </Typography>
                              </TableCell>

                              <TableCell align="center">
                                <IconButton onClick={() => handleOpenModal(row)}>
                                  <Visibility />
                                </IconButton>
                              </TableCell>

                              <TableCell align="center">
                                <IconButton
                                  onClick={() => openDeleteDialog(row)}
                                  disabled={isDeletingThis}
                                  sx={{ color: isDeletingThis ? "#94a3b8" : "#dc2626" }}
                                >
                                  {isDeletingThis ? <CircularProgress size={18} /> : <DeleteOutline />}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            background: "#ffffff",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.2,
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.2 }}>
              Damage Entry Details
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#cbd5e1", mt: 0.4 }}>
              Complete damage record information
            </Typography>
          </Box>

          <IconButton
            onClick={handleCloseModal}
            disabled={deletingId === selectedEntry?._id}
            sx={{
              color: "#ffffff",
              background: "rgba(255,255,255,0.12)",
              "&:hover": {
                background: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 3,
            background: "#f8fafc",
            borderColor: "#e2e8f0",
          }}
        >
          {selectedEntry ? (
            <Grid container spacing={2.2}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    height: "100%",
                  }}
                >
                  <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                    Date
                  </Typography>
                  <Typography sx={{ color: "#0f172a", fontWeight: 900, mt: 0.5 }}>
                    {getEntryDate(selectedEntry)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    height: "100%",
                  }}
                >
                  <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                    Type
                  </Typography>
                  <Typography sx={{ color: "#0f172a", fontWeight: 900, mt: 0.5 }}>
                    {isProductEntry(selectedEntry) ? "Product" : "Raw Material"}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    height: "100%",
                  }}
                >
                  <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                    Quantity
                  </Typography>
                  <Typography sx={{ color: "#0f172a", fontWeight: 900, mt: 0.5 }}>
                    {Number(selectedEntry?.quantity || 0)} {getEntryUnit(selectedEntry)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: 3,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a", mb: 1.5 }}>
                    {isProductEntry(selectedEntry) ? "Product" : "Raw Material"}
                  </Typography>

                  <Box
                    sx={{
                      p: 1.8,
                      borderRadius: 2.5,
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                      Name
                    </Typography>
                    <Typography sx={{ color: "#0f172a", fontWeight: 900, mt: 0.4 }}>
                      {isProductEntry(selectedEntry)
                        ? selectedEntry?.product?.name || "N/A"
                        : selectedEntry?.rawMaterial?.name || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {isProductEntry(selectedEntry) && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2.2,
                      borderRadius: 3,
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a", mb: 1.5 }}>
                      Addons
                    </Typography>

                    {(selectedEntry?.addons || []).length === 0 ? (
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          background: "#f8fafc",
                          border: "1px dashed #cbd5e1",
                        }}
                      >
                        <Typography sx={{ color: "#64748b", fontWeight: 800 }}>
                          No addons
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          overflow: "hidden",
                          borderRadius: 2.5,
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Box
                          component="table"
                          sx={{
                            width: "100%",
                            borderCollapse: "collapse",
                            background: "#ffffff",
                          }}
                        >
                          <thead>
                            <tr style={{ background: "#f1f5f9" }}>
                              <th
                                style={{
                                  padding: "12px 14px",
                                  textAlign: "left",
                                  color: "#334155",
                                  fontSize: 13,
                                  fontWeight: 900,
                                  borderBottom: "1px solid #e2e8f0",
                                }}
                              >
                                Addon Title
                              </th>
                              <th
                                style={{
                                  padding: "12px 14px",
                                  textAlign: "left",
                                  color: "#334155",
                                  fontSize: 13,
                                  fontWeight: 900,
                                  borderBottom: "1px solid #e2e8f0",
                                }}
                              >
                                Addon Value
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {selectedEntry.addons.map((a, i) => (
                              <tr key={i}>
                                <td
                                  style={{
                                    padding: "12px 14px",
                                    borderBottom:
                                      i === selectedEntry.addons.length - 1
                                        ? "none"
                                        : "1px solid #e2e8f0",
                                    color: "#0f172a",
                                    fontWeight: 700,
                                  }}
                                >
                                  {a?.addonTitle || "N/A"}
                                </td>

                                <td
                                  style={{
                                    padding: "12px 14px",
                                    borderBottom:
                                      i === selectedEntry.addons.length - 1
                                        ? "none"
                                        : "1px solid #e2e8f0",
                                    color: "#0f172a",
                                    fontWeight: 700,
                                  }}
                                >
                                  {a?.addonValueTitle || "N/A"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: 3,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a", mb: 1.5 }}>
                    Attachments
                  </Typography>

                  {getEntryAttachments(selectedEntry).length === 0 ? (
                    <Box
                      sx={{
                        p: 2,
                        border: "1px dashed #cbd5e1",
                        borderRadius: 2.5,
                        background: "#f8fafc",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ImageOutlined sx={{ color: "#64748b" }} />

                        <Typography sx={{ color: "#64748b", fontWeight: 800 }}>
                          No attachments available
                        </Typography>
                      </Stack>
                    </Box>
                  ) : (
                    <Stack direction="row" spacing={1.4} useFlexGap flexWrap="wrap">
                      {getEntryAttachments(selectedEntry).map((a, i) => (
                        <Box
                          key={a?._id || i}
                          component="img"
                          src={a?.attachment}
                          alt="damage attachment"
                          onClick={() => setPreviewImage(a?.attachment)}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          sx={{
                            width: 112,
                            height: 112,
                            borderRadius: 3,
                            objectFit: "cover",
                            cursor: "pointer",
                            border: "1px solid #e2e8f0",
                            background: "#f8fafc",
                            transition: "0.2s ease",
                            boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: "0 12px 24px rgba(15, 23, 42, 0.16)",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2.2,
                    borderRadius: 3,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a", mb: 1.5 }}>
                    Remark
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid #e2e8f0",
                      borderRadius: 2.5,
                      background: "#f8fafc",
                    }}
                  >
                    <Typography sx={{ color: "#0f172a", fontWeight: 800 }}>
                      {selectedEntry?.remark || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            fontWeight: 900,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#0f172a",
            color: "#ffffff",
          }}
        >
          Attachment Preview

          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              color: "#ffffff",
              background: "rgba(255,255,255,0.12)",
              "&:hover": {
                background: "rgba(255,255,255,0.2)",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 2,
            background: "#f8fafc",
            borderColor: "#e2e8f0",
          }}
        >
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="attachment preview"
              sx={{
                width: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: 3,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openDeleteConfirm} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          Confirm Delete

          <IconButton onClick={closeDeleteDialog} disabled={deletingId === deleteCandidate?._id}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
            Are you sure you want to delete this damage entry?
          </Typography>

          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 800 }}>
              Type: {isProductEntry(deleteCandidate) ? "Product" : "Raw Material"}
            </Typography>

            <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 800, mt: 0.5 }}>
              Name: {candidateName}
            </Typography>

            <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.5 }}>
              This action cannot be undone.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={closeDeleteDialog}
              disabled={deletingId === deleteCandidate?._id}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => performDeleteDamageEntry(deleteCandidate)}
              disabled={!deleteCandidate?._id || deletingId === deleteCandidate?._id}
              startIcon={
                deletingId === deleteCandidate?._id ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  <DeleteOutline />
                )
              }
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                textTransform: "none",
                backgroundColor: "#dc2626",
                "&:hover": { backgroundColor: "#b91c1c" },
              }}
            >
              {deletingId === deleteCandidate?._id ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default DamageReport;